import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, ConfirmDialog, Input, Modal, Select, Textarea, useToast } from '../../components/common';
import '../../components/ThreePanelLayout.css'; // detail panel still uses .three-panel__detail-* styles
import { KeyRound, UserRound, Users, Plus } from 'lucide-react';
import HealthPanel from './HealthPanel';
import ReadinessPanel from './ReadinessPanel';
import DraftBar from './DraftBar';
import { LifecycleStrip, NextBar } from '../../components/dev/AppLifecycle';
import AppPageNav from '../../components/dev/AppPageNav';
import AppFleet from '../../components/dev/AppFleet';
import PanelActions, { PanelActionsSlot } from '../../components/dev/PanelActions';
import './AppPanels.css';
import { deriveState, deriveBlocking, stagedPages } from '../../utils/appState';
import { loadFleetSignals, factsFor } from '../../utils/fleetFacts';
import { copyText } from '../../utils/clipboard';
import useLatest from '../../utils/useLatest';
import { appPagePath, APP_PAGES } from '../../devmap';
import AppLogoField from './AppLogoField';
import CategoryField from './CategoryField';
import TabGroups from './TabGroups';
import EarningsPanel from './EarningsPanel';
import CruxTab from './CruxTab';
import CapabilitiesTab from './CapabilitiesTab';
import AccessTab from './AccessTab';
import PricingTab from './PricingTab';
import InlineField, { ReadOnlyField } from './InlineField';
import SearchField from '../../components/SearchField';
import PortalPage from '../../components/PortalPage';
import '@paradigm/permissions/components/permissions.css';
import { diffVersions, changeKind, eventKind } from '../../utils/versionDiff';
import { useGroup } from '../../contexts/GroupContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { NUCLEUS_URL } from '../../services/apiConfig';
import './MyAppsTab.css';
import './AppDetail.css';
import '../../styles/PortalPage.css';

// Remember the active list tab across remounts (the page remounts when you open
// an app's route and navigate back), so returning lands on the same tab.

function MyAppsTab({ thirdPartyApps: propApps, myPlugins: propPlugins, appSchemas: propSchemas, availablePlugins: propAvailPlugins, loading: propLoading, loadData: propLoadData, mode, routed = false }) {
  const toast = useToast();
  const { groups, activeGroupId } = useGroup();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { appId: routeAppId } = useParams();
  const { search: routeSearch } = useLocation();

  // Self-contained mode: when rendered as a standalone page (no props),
  // manage data loading internally.
  const standalone = propApps === undefined;
  const [ownApps, setOwnApps] = useState([]);
  const [ownPlugins, setOwnPlugins] = useState([]);
  const [ownSchemas, setOwnSchemas] = useState([]);
  const [ownAvailPlugins, setOwnAvailPlugins] = useState([]);
  const [ownLoading, setOwnLoading] = useState(standalone);

  const ownLoadData = useCallback(async () => {
    setOwnLoading(true);
    try {
      const myAppsUrl = activeGroupId
        ? `/third-party/my-apps?group_id=${activeGroupId}`
        : '/third-party/my-apps';
      const [appsRes, pluginsData, schemasRes, allPluginsData] = await Promise.all([
        api.request(myAppsUrl).catch(() => ({ apps: [] })),
        // A group context used to resolve to [] — the endpoint had no group
        // branch, so group-owned plugins existed and were listed nowhere.
        api.getMyPlugins(activeGroupId).catch(() => []),
        api.request('/schemas?limit=1000&group_by_uri=true').catch(() => ({ schemas: [] })),
        api.getPlugins({}).catch(() => ({ plugins: [] })),
      ]);
      setOwnApps(appsRes.apps || []);
      setOwnPlugins(pluginsData);
      setOwnSchemas(schemasRes.schemas || []);
      setOwnAvailPlugins(allPluginsData.plugins || []);
    } catch (e) { /* ignore */ }
    setOwnLoading(false);
  }, [activeGroupId]);

  useEffect(() => { if (standalone) ownLoadData(); }, [standalone, ownLoadData]);

  const thirdPartyApps = standalone ? ownApps : propApps;
  const myPlugins = standalone ? ownPlugins : propPlugins;
  const appSchemas = standalone ? ownSchemas : propSchemas;
  const availablePlugins = standalone ? ownAvailPlugins : propAvailPlugins;
  const loading = standalone ? ownLoading : propLoading;
  const loadData = standalone ? ownLoadData : propLoadData;

  // True if the current user can transfer this app OR plugin (creator, or admin
  // of the group that owns it). Mirrors the backend rule in
  // third_party/discovery.py — which never asked whether the row was a plugin,
  // because a plugin IS a third_parties row with is_plugin set. Only this gate
  // ever excluded them, and the effect was a Delete button with no Transfer
  // beside it: the one irreversible action, and not the reversible one.
  //
  // `user.id`, not `user.user_id` — the identity payload has always keyed it
  // `id`. Comparing an absent key to an absent field (to_dict didn't serialize
  // created_by_user_id either) made this `undefined === undefined`, so the gate
  // passed for everything and only LOOKED like it was checking.
  const canTransfer = (item) => {
    if (!item) return false;
    if (item.group_id) return groups.find(g => g.id === item.group_id)?.my_role === 'admin';
    const me = user?.id || user?.user_id;
    return Boolean(me && item.created_by_user_id && item.created_by_user_id === me);
  };

  // Selection is URL-driven when `routed` (each app has its own page at
  // /applications/:appId), else it's local state (embedded usage).
  const [internalSelectedId, setInternalSelectedId] = useState(null);
  const selectedId = routed ? (routeAppId || null) : internalSelectedId;
  const openDetail = useCallback((id) => {
    if (routed) navigate(`/applications/${id}`);
    else setInternalSelectedId(id);
  }, [routed, navigate]);
  const closeDetail = useCallback(() => {
    if (routed) navigate('/applications/all');
    else setInternalSelectedId(null);
  }, [routed, navigate]);
  const [search, setSearch] = useState('');
  // The panel head's action slot; panels portal their main action into it.
  const [actionSlot, setActionSlot] = useState(null);
  // Apps only: plugins have their own page in the frame's Apps column, so the
  // list no longer carries an Applications/Plugins tab strip of its own.
  const listTab = mode === 'plugin' ? 'plugin' : 'app';
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [epActionDialog, setEpActionDialog] = useState({ isOpen: false, pendingPayload: null });

  // Which section of the open app is showing. Editing always sits on Overview:
  // the form saves as one payload and the backend snapshots a version per PUT,
  // so a save per tab would bury the changes that matter in version history.
  const [detailTab, setDetailTab] = useState('overview');
  // Bumped whenever a test user is added, renamed or deleted, so the sandbox
  // groups panel re-reads the names it shows.
  const [testUsersRev, setTestUsersRev] = useState(0);
  // Routed, the page showing IS the address (`?panel=`): the frame's page
  // column lists this app's pages and lights the one in the URL, so every
  // switch — a tile, the Next bar, a version chip — moves the URL and the
  // effect below follows it. Embedded (no route), it is local state.
  const showTab = useCallback((id) => {
    if (!routed || !selectedId) { setDetailTab(id); return; }
    navigate(appPagePath(selectedId, id));
  }, [routed, selectedId, navigate]);

  // Version history — lifted here so it's visible in both view and edit mode
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  // {schema id: name} for the ids this app's DLR history mentions — the diff
  // renders permission scope, and a UUID there is not a thing anyone can read.
  const [schemaNames, setSchemaNames] = useState({});
  const [versionsLoading, setVersionsLoading] = useState(false);

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferType, setTransferType] = useState('user'); // 'user' | 'group' | 'personal'
  const [transferTarget, setTransferTarget] = useState(''); // user_id or group_id
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState('');

  // User search for transfer-to-user
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // { id, username, email }
  const userSearchTimer = useRef(null);

  // Combine apps + plugins into a unified list
  const allItems = useMemo(() => {
    const pluginClientIds = new Set(myPlugins.map(p => p.client_id || p.id));
    const apps = thirdPartyApps
      .filter(app => !pluginClientIds.has(app.client_id) && !pluginClientIds.has(app.id))
      .map(app => ({ ...app, _type: 'app' }));
    const plugins = myPlugins.map(p => ({ ...p, _type: 'plugin' }));
    return [...apps, ...plugins];
  }, [thirdPartyApps, myPlugins]);

  // Build grouped display: apps first, each followed by their owned plugins, then standalone plugins
  const groupedItems = useMemo(() => {
    const apps = allItems.filter(i => i._type === 'app');
    const ownedPlugins = allItems.filter(i => i._type === 'plugin' && i.parent_app_id);
    const standalonePlugins = allItems.filter(i => i._type === 'plugin' && !i.parent_app_id);

    const result = [];
    apps.forEach(app => {
      result.push(app);
      ownedPlugins.filter(p => p.parent_app_id === app.id).forEach(p => result.push({ ...p, _nested: true }));
    });
    standalonePlugins.forEach(p => result.push(p));
    return result;
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const s = search.toLowerCase().trim();
    // When filtering, fall back to flat list so owned plugins are still reachable
    const base = search ? allItems : groupedItems;
    return base.filter(item => {
      if (s && !item.name?.toLowerCase().includes(s) && !(item.description || '').toLowerCase().includes(s)) return false;
      return true;
    });
  }, [allItems, groupedItems, search]);

  const selectedItem = allItems.find(i => i.id === selectedId) || null;

  // A plugin's page is its workspace, /plugins/:id. An old link to one here
  // (or a bookmark) lands there instead of on an app's tabs.
  useEffect(() => {
    if (selectedItem?._type === 'plugin') navigate(`/plugins/${selectedItem.id}`, { replace: true });
  }, [selectedItem, navigate]);


  // --- Handlers ---

  const copyToClipboard = async (text, label) => {
    if (await copyText(text)) toast.success(`${label} copied to clipboard.`);
    else toast.error(`Could not copy the ${label} — select it and copy it by hand.`);
  };

  /**
   * Save a single field. The update endpoint applies `if 'field' in data`, and
   * rewrites the DLR only when a `yaml` key is present — so a patch naming one
   * field cannot disturb the app's permissions, which the whole-form save could.
   * Every update snapshots a version, so the summary says which field moved.
   */
  const [draft, setDraft] = useState(null);
  const beginDraft = useLatest();
  const loadDraft = useCallback(async () => {
    const isCurrent = beginDraft();
    if (!selectedId) { setDraft(null); return; }
    let next = null;
    try {
      next = await api.request(`/third-party/${selectedId}/draft`);
    } catch {
      next = null;   // an older backend simply has no drafts
    }
    // Another app opened (or a newer read began) while this one was out.
    if (isCurrent()) setDraft(next);
  }, [selectedId, beginDraft]);
  useEffect(() => { loadDraft(); }, [loadDraft]);

  // The facts the app's state is derived from — the same loader Home uses
  // (utils/fleetFacts.js), so the lifecycle strip here and Home's pipeline
  // can never place this app in different states.
  //
  // An app's own page REUSES the last fleet load (Home's, or the list's) while
  // it is fresh: the scores call re-runs every app's checks, and opening one app
  // used to pay for the whole fleet each time. The list always reads afresh.
  //
  // Real (non-test) people come in the same load: the overview counts them
  // exactly per app (`real_users_count`), so Testing vs Live here is the same
  // number Home and the Apps list read — no per-app request of its own.
  const [signals, setSignals] = useState(null);
  const onAppPage = !!selectedId;
  const beginSignals = useLatest();
  useEffect(() => {
    const isCurrent = beginSignals();
    loadFleetSignals(activeGroupId, { reuse: onAppPage }).then((sig) => { if (isCurrent()) setSignals(sig); });
  }, [activeGroupId, onAppPage, beginSignals]);

  // After something that moves the app's facts — a commit, a test user, a
  // price — re-read only what that action can have changed.
  const refreshSignals = useCallback(async (parts) => {
    const isCurrent = beginSignals();
    const sig = await loadFleetSignals(activeGroupId, { refresh: parts });
    if (isCurrent()) setSignals(sig);
  }, [activeGroupId, beginSignals]);

  const saveField = async (patch, label) => {
    if (!selectedItem) return;
    try {
      // Staged, not applied: edits are collected across tabs and become one
      // version when committed. `paradigm changes` reads the same draft.
      await api.request(`/third-party/${selectedItem.id}/draft`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      toast.success(`${label || 'Field'} staged.`);
      await loadDraft();
    } catch (err) {
      toast.error(err.message || 'Could not save.');
      throw err;   // keeps the row open with the draft intact
    }
  };

  /** Re-submit a rollback with the developer's chosen ep_action. */
  const submitWithEpAction = async (action) => {
    const dialog = epActionDialog;
    setEpActionDialog({ isOpen: false, pendingPayload: null });
    if (dialog.rollbackVersion == null) return;
    try {
      const result = await api.rollbackApp(selectedItem.id, dialog.rollbackVersion, action);
      const effects = result?.dlr_effects || {};
      if (effects.action_taken === 'cancel' && effects.cancelled_ep_count > 0) {
        toast.warning(`Rolled back to v${dialog.rollbackVersion}. ${effects.cancelled_ep_count} authorization(s) cancelled.`);
      } else {
        toast.success(`Rolled back to v${dialog.rollbackVersion}.`);
      }
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to save changes');
    }
  };

  /** Rollback to a specific version. */
  const handleRollback = async (version) => {
    if (!selectedItem) return;
    try {
      await api.rollbackApp(selectedItem.id, version);
      toast.success(`Rolled back to v${version}.`);
      loadData();
    } catch (err) {
      if (err.code === 'EP_ACTION_REQUIRED') {
        setEpActionDialog({ isOpen: true, pendingPayload: true, rollbackVersion: version });
      } else {
        toast.error(err.message || 'Rollback failed');
      }
    }
  };

  // No "already loading" early return: it made app B's load bail while app A's
  // was out, and A's versions then landed on B. The newest load wins instead.
  const beginVersions = useLatest();
  const loadVersions = useCallback(async () => {
    const isCurrent = beginVersions();
    if (!selectedItem || selectedItem._type !== 'app') return;
    setVersionsLoading(true);
    try {
      const result = await api.getAppVersions(selectedItem.id);
      if (!isCurrent()) return;
      setVersions(result.versions || []);
      setSchemaNames(result.schema_names || {});
    } catch { /* ignore */ }
    if (isCurrent()) setVersionsLoading(false);
  }, [selectedItem?.id, beginVersions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset version state when switching items
  useEffect(() => {
    setVersionsOpen(false);
    setVersions([]);
  }, [selectedId]);

  // Lazy-load versions when panel is first opened
  useEffect(() => {
    if ((versionsOpen || detailTab === 'versions') && versions.length === 0) loadVersions();
  }, [versionsOpen, detailTab, loadVersions, versions.length]);

  const handleDelete = (item) => {
    const label = item._type === 'app' ? 'app' : 'plugin';
    setConfirmDialog({
      isOpen: true,
      title: `Delete ${label === 'app' ? 'App' : 'Plugin'}`,
      message: `Delete "${item.name}"? This cannot be undone.${item._type === 'app' ? ' All user authorizations will be revoked.' : ''}`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          if (item._type === 'app') {
            await api.request(`/apps/${item.id}`, { method: 'DELETE' });
          } else {
            await api.deletePlugin(item.id);
          }
          toast.success(`${item.name} deleted.`);
          if (selectedId === item.id) closeDetail();
          loadData();
        } catch (err) {
          toast.error(err.message || `Failed to delete ${label}`);
        }
      },
    });
  };

  // Debounced user search
  const handleUserSearchChange = (q) => {
    setUserSearchQuery(q);
    setSelectedUser(null);
    setTransferTarget('');
    clearTimeout(userSearchTimer.current);
    if (!q.trim()) { setUserSearchResults([]); return; }
    userSearchTimer.current = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const results = await api.searchUsers(q.trim(), 10);
        setUserSearchResults(results);
      } catch {
        setUserSearchResults([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
  };

  const selectTransferUser = (u) => {
    setSelectedUser(u);
    setTransferTarget(u.user_id);
    setUserSearchQuery(u.username || '');
    setUserSearchResults([]);
  };

  const openTransferModal = (item) => {
    setTransferType(item.group_id ? 'personal' : 'user');
    setTransferTarget('');
    setTransferError('');
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSelectedUser(null);
    setShowTransferModal(true);
  };

  const handleTransfer = async () => {
    if (!selectedItem) return;
    setTransferLoading(true);
    setTransferError('');
    try {
      const payload =
        transferType === 'user' ? { to_user_id: transferTarget } :
        transferType === 'group' ? { to_group_id: transferTarget } :
        { to_personal: true };
      await api.request(`/third-party/${selectedItem.id}/transfer`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      toast.success('Ownership transferred successfully.');
      setShowTransferModal(false);
      closeDetail();
      loadData();
    } catch (err) {
      setTransferError(err.message || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };



  // Which page a staged field belongs to (utils/appState.js stagedPages), so the
  // marker lands on the page you would go to in order to see the change.
  const stagedPanels = useMemo(() => stagedPages(draft), [draft]);

  /**
   * The sections, grouped by what KIND of thing each one is.
   *
   * Nine tabs in one strip mixed three unrelated jobs, and the most
   * consequential fact about a tab — whether saving changes what users see —
   * was not written anywhere. The group carries it now, once, for everything
   * underneath.
   *
   * Only an app opens here. A plugin has its own workspace at /plugins/:id, so
   * none of these tabs (Access, Your API, Usage) is offered for one.
   */
  const tabGroups = useMemo(() => {
    if (!selectedItem || selectedItem._type !== 'app') return [];
    const staged = (id) => stagedPanels.has(id);
    const pending = draft?.has_changes
      ? (draft.count || (draft.changes || []).length)
      : 0;

    const groups = [
      {
        id: 'declare',
        label: 'Declare',
        why: 'Edits stage — one commit, one version',
        badge: pending ? `${pending} staged` : null,
        tabs: [
          // "Overview" promised a summary and delivered the app record: its
          // identifiers and the fields that describe it.
          { id: 'overview', label: 'Details', staged: staged('overview') },
          { id: 'access', label: 'Access', staged: staged('access') },
          // Pricing sits next to Access because they are the two halves of one
          // consent card: what the app takes, and what it costs.
          { id: 'pricing', label: 'Pricing', staged: staged('pricing') },
          { id: 'crux', label: 'CRUX', staged: staged('crux') },
        ],
      },
      {
        id: 'develop',
        label: 'Develop',
        why: 'Credentials and a sandbox — invisible to users',
        tabs: [
          // These two were stacked vertically inside one "Testing" panel. They
          // are separate jobs done at different times, and a panel you scroll
          // past to reach the other one is not a home for either.
          { id: 'keys', label: 'Keys' },
          { id: 'testusers', label: 'Test users' },
          // Your app's own published node graph — the catalog a user's own
          // node can later link to. Dev-portal only, same as the two above:
          // there is no nucleus-facing view of this at all.
          { id: 'graph', label: 'Catalog' },
          // "API" read as Paradigm's API. It is this app's own endpoints,
          // published so agents can discover them.
          { id: 'api', label: 'Your API' },
        ],
      },
      {
        id: 'evidence',
        label: 'Evidence',
        why: 'Measured, read-only',
        tabs: [
          // Two different questions, still two tabs: Usage is "is it being used,
          // and is it up?"; Evals is "is it fit to be on the identity OS at
          // all?". They move on different timescales — usage hourly, evals only
          // when the spec changes — so folding them together would average two
          // unrelated rhythms into one stale number.
          { id: 'health', label: 'Usage' },
          { id: 'readiness', label: 'OS-Evals' },
          // Earnings had no home on the app at all: an app's revenue was only
          // ever visible under the developer-level Money page, mixed with every
          // other app's. It reads beside this app's usage, which is the question
          // it actually answers.
          { id: 'earnings', label: 'Earnings' },
          { id: 'versions', label: 'Versions' },
        ],
      },
    ];
    return groups.filter(g => g.tabs.length > 0);
  }, [selectedItem, stagedPanels, draft]);

  // Opening a different app should land on Details, not wherever you last were —
  // unless whoever sent you here named the panel. The home worklist names each
  // app's blocking item, and naming it is only useful if one press reaches it,
  // so `?panel=` lands on that section instead of making you go and find it.
  //
  // Applied ONCE per (app, query) pair. Keying the effect on `tabGroups` would
  // be the obvious way to wait for the panel list to arrive, but that object is
  // rebuilt whenever the draft changes — so staging an edit would throw you back
  // to Details mid-sentence.
  const landedRef = useRef(null);
  useEffect(() => {
    const key = `${selectedId}|${routeSearch}`;
    if (landedRef.current === key) return;
    const wanted = new URLSearchParams(routeSearch).get('panel');
    if (!wanted) {
      landedRef.current = key;
      setDetailTab('overview');
      return;
    }
    // The groups are empty until the app loads. Wait rather than falling back,
    // or a cold load on a deep link always lands on the wrong panel.
    if (tabGroups.length === 0) return;
    landedRef.current = key;
    const known = tabGroups.some(g => g.tabs.some(t => t.id === wanted));
    setDetailTab(known ? wanted : 'overview');
  }, [selectedId, routeSearch, tabGroups]);

  // --- Detail Panel ---
  // Where the app stands and the one thing to do next. Routed, it is the first
  // band of Overview; embedded (no frame), it stands above the tabs as before.
  const facts = selectedItem ? factsFor(selectedItem, signals, draft) : null;
  const blocking = selectedItem ? deriveBlocking(selectedItem, facts) : null;
  const appState = selectedItem ? deriveState(selectedItem, facts) : null;
  // The commit bar for staged edits. It stands wherever you can stage one —
  // Overview's band, and the foot of every other page — because an edit staged
  // on Access that can only be committed from Overview is an edit you lose
  // track of. Committing re-reads every fact the app's state stands on.
  const draftBar = selectedItem && draft?.has_changes ? (
    <DraftBar
      appId={selectedItem.id}
      draft={draft}
      specVersion={selectedItem.spec_version}
      onChanged={async () => {
        await loadDraft();
        await loadData();
        await refreshSignals(['overview', 'scores', 'payout']);
      }}
    />
  ) : null;
  // Signals not in yet: the Next bar must not say "Nothing waiting." on facts
  // it does not have.
  const checking = !signals;
  const whereItStands = selectedItem ? (
    <section className="appwhere">
      <h4 className="appwhere__title">Where it stands</h4>
      <LifecycleStrip state={appState} />
      {/* A staged draft is the next thing, and the DraftBar owns its commit
          flow; otherwise the one blocker, with the press that reaches it. */}
      {draftBar || (
        <NextBar
          blocking={blocking}
          checking={checking}
          onAct={(b) => (b.href ? navigate(b.href) : showTab(b.panel || 'overview'))}
        />
      )}
    </section>
  ) : null;

  // Marks beside the app's pages: a dot where something needs you, else a
  // quiet count.
  const score = selectedItem ? signals?.scoreByApp?.[String(selectedItem.id)] : null;
  const checksFailing = !!(score?.checks?.total && score.checks.passed < score.checks.total);
  const pageMarks = selectedItem ? {
    access: blocking?.code === 'no_dlr' ? true : String((selectedItem.dlr_inputs || []).length || ''),
    crux: blocking?.code === 'no_crux' ? true : undefined,
    testusers: String((selectedItem.test_user_ids || []).length || ''),
    readiness: checksFailing || score?.blocked === 'no_crux',
    versions: selectedItem.spec_version ? `v${selectedItem.spec_version}` : undefined,
  } : {};
  const stagedMarks = [...stagedPanels];

  // The overview's numbers: what the fleet endpoints already say about this app.
  // Mixed periods (people is a count now, calls a 30-day sum), so each figure
  // names its own window and the band does not claim one.
  const calls30 = selectedItem ? signals?.callsByApp?.[String(selectedItem.id)] : undefined;
  const atAGlance = selectedItem && routed ? (
    <section className="appwhere appstats" aria-label="At a glance">
      <h4 className="appwhere__title">At a glance</h4>
      <dl className="appstats__grid">
        <div><dt>People using it</dt><dd>{facts?.realUsers ?? '—'}</dd>
          {facts?.testUsers > 0 && <span>{facts.testUsers} test {facts.testUsers === 1 ? 'user' : 'users'} too</span>}</div>
        <div><dt>API calls, 30 days</dt><dd>{calls30 == null ? '—' : calls30.toLocaleString()}</dd>
          {calls30 > 0 && <span>{Math.round(calls30 / 30).toLocaleString()} a day</span>}</div>
        <div><dt>OS-Evals</dt><dd>{score?.checks?.total ? `${score.checks.passed} of ${score.checks.total}` : '—'}</dd>
          {score?.checks?.total > 0 && <span>checks met</span>}</div>
        <div><dt>Version</dt><dd>v{selectedItem.spec_version || 1}</dd>
          <span>{selectedItem.deployment_mode === 'public' ? 'Public' : 'Incubator'}</span></div>
      </dl>
    </section>
  ) : null;

  // Keyed by the app: every panel below starts afresh for each app, so a
  // panel's in-flight read for the last app can never write into this one.
  const panels = selectedItem ? (
      <React.Fragment key={selectedItem.id}>
        {detailTab === 'overview' && routed && whereItStands}
        {detailTab === 'overview' && atAGlance}
        {detailTab === 'overview' && (
          <ViewDetail
            // Remount on every save (spec_version bumps) so the read-mode permission grid
            // reflects the just-saved DLR instead of a stale snapshot held by its children.
            key={`view-${selectedItem.id}-${selectedItem.spec_version ?? 0}`}
            item={selectedItem}
            copyToClipboard={copyToClipboard}
            saveField={saveField}
            stagedFields={(draft?.changes || []).map(c => c.field)}
            onLogoSaved={loadData}
          />
        )}

        {detailTab === 'access' && (
          <AccessTab app={selectedItem} appSchemas={appSchemas} availablePlugins={availablePlugins}
                     onSaved={loadData} onStaged={loadDraft} />
        )}
        {detailTab === 'pricing' && (
          <PricingTab app={selectedItem} onStaged={async () => { await loadDraft(); await refreshSignals(['payout']); }} />
        )}
        {detailTab === 'crux' && (
          <CruxTab appId={selectedItem.id} appName={selectedItem.name} specVersion={selectedItem.spec_version} onStaged={loadDraft} />
        )}
        {detailTab === 'api' && (
          <CapabilitiesTab appId={selectedItem.id} />
        )}
        {detailTab === 'health' && (
          <HealthPanel appId={selectedItem.id} websiteUrl={selectedItem.website_url} />
        )}
        {detailTab === 'readiness' && (
          <ReadinessPanel appId={selectedItem.id} onGoToCrux={() => showTab('crux')} />
        )}
        {detailTab === 'versions' && (
          <VersionsPanel
            versions={versions}
            loading={versionsLoading}
            specVersion={selectedItem.spec_version}
            onRollback={handleRollback}
            appId={selectedItem.id}
            live={selectedItem}
            schemaNames={schemaNames}
          />
        )}
        {/* Testing was three unrelated things stacked in one scroll. The
            commands for picking an app up on a machine moved to Start
            developing → Continue, where somebody actually needs them; the two
            panels that remain are separate jobs and now have separate names. */}
        {detailTab === 'keys' && (
          <KeyringPanel appId={selectedItem.id} appName={selectedItem.name} />
        )}
        {detailTab === 'testusers' && (
          <>
            <TestUsersPanel
              appId={selectedItem.id}
              initialUserIds={selectedItem.test_user_ids || []}
              onChanged={async () => {
                // A test user changes the app record (test_user_ids), the
                // authorization count, and the names the groups panel shows.
                setTestUsersRev((n) => n + 1);
                await loadData();
                await refreshSignals(['overview']);
              }}
            />
            <TestGroupsPanel appId={selectedItem.id} testUserIds={selectedItem.test_user_ids || []} rev={testUsersRev} />
          </>
        )}
        {detailTab === 'graph' && (
          <AppGraphPanel appId={selectedItem.id} />
        )}
        {detailTab === 'earnings' && (
          <EarningsPanel appId={selectedItem.id} />
        )}
      </React.Fragment>
  ) : null;

  const detail = selectedItem?._type === 'app' ? (
    <div className="appdetail">
      <header className="appdetail__head">
        <div className="appdetail__title">
          {/* A plain title. It was a native <select> doubling as an app
              switcher, so pressing the app's own name opened a menu — the
              surprise was worse than the two steps it saved. Apps are switched
              from the list. */}
          <h1 className="appdetail__name">{selectedItem.name}</h1>
          {/* One chip grammar; colour only where a state is abnormal. The
              version is a link, the one chip with somewhere to go. */}
          <div className="appdetail__meta">
            {routed && appState && <span className="appdetail__chip appdetail__chip--state">{appState.label}</span>}
            {!routed && <span className="appdetail__chip">Application</span>}
            {selectedItem.is_active === false && (
              <span className="appdetail__chip appdetail__chip--warn">Inactive</span>
            )}
            <span className="appdetail__chip">
              {selectedItem.deployment_mode === 'public' ? 'Public' : 'Incubator'}
            </span>
            {selectedItem.spec_version > 1 && (
              <button
                className="appdetail__chip appdetail__chip--link"
                onClick={() => showTab('versions')}
                title="Version history"
              >
                v{selectedItem.spec_version}
              </button>
            )}
          </div>
        </div>
          <div className="appdetail__actions">
            {canTransfer(selectedItem) && (
              <Button variant="secondary" size="sm" onClick={() => openTransferModal(selectedItem)}>Transfer</Button>
            )}
            <Button variant="danger" size="sm" onClick={() => handleDelete(selectedItem)}>Delete</Button>
          </div>
        {!routed && selectedItem.description && <p className="appdetail__desc">{selectedItem.description}</p>}
      </header>

      {routed ? (
        /* The app's pages beside one panel: the list on the left (grouped Set
           up / Build / Results), the open page on the right. */
        <div className="appdetail__layout">
          <AppPageNav appId={selectedItem.id} active={detailTab} marks={pageMarks} staged={stagedMarks} />
          <section className="appdetail__body apppanel">
            {(() => {
              const here = APP_PAGES.find((p) => p.id === detailTab) || APP_PAGES[0];
              return (
                <header className="appdetail__panelhead">
                  <div>
                    <h2>{here.label}</h2>
                    <p>{here.about}</p>
                  </div>
                  <div className="appdetail__panelacts" ref={setActionSlot} />
                </header>
              );
            })()}
            <PanelActionsSlot.Provider value={actionSlot}>
              <div className="apppanel__content">
                {panels}
                {/* Overview carries the bar in its band; every other page at its foot. */}
                {detailTab !== 'overview' && draftBar && <div className="apppanel__draft">{draftBar}</div>}
              </div>
            </PanelActionsSlot.Provider>
          </section>
        </div>
      ) : (
        <>
          {whereItStands}
          <TabGroups groups={tabGroups} active={detailTab} onSelect={setDetailTab} />
          <div className="appdetail__body">{panels}</div>
        </>
      )}
    </div>
  ) : null;

  // --- Render ---
  return (
    // The detail view brings its own header (app name, chips, actions), so the
    // frame titles the list only.
    <PortalPage
      width="full"
      title={selectedItem ? null : 'Apps'}
      lede={selectedItem ? null : 'Everything you build. Open one to see where it stands and what it needs next.'}
      actions={!selectedItem && routed && listTab === 'app' ? (
        <>
          <Button variant="primary" onClick={() => navigate('/create?lane=new')}>New app</Button>
          <Button variant="secondary" onClick={() => navigate('/plugins/new')}>New plugin</Button>
        </>
      ) : null}
    >
      {selectedItem ? (
        <div className="myapps-detail">{detail}</div>
      ) : (
        <div className="myapps">

          {allItems.length > 6 && (
            <div className="myapps__search">
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder={`Search ${listTab === 'plugin' ? 'plugins' : 'applications'}…`}
              />
            </div>
          )}

          {loading ? (
            <p className="myapps__loading">Loading…</p>
          ) : routed && listTab === 'app' && filteredItems.some(i => i._type === 'app') ? (
            <AppFleet apps={filteredItems.filter(i => i._type === 'app')} signals={signals} schemaNames={schemaNames} />
          ) : (() => {
            const items = filteredItems.filter(i => i._type === listTab);
            if (items.length === 0) {
              return (
                <div className="myapps__empty">
                  {search ? `No ${listTab === 'plugin' ? 'plugins' : 'applications'} match your search.` : (
                    // Into first-run onboarding, already on the right kind.
                    <>
                      <p>{listTab === 'plugin' ? 'No plugins yet.' : 'No apps yet.'}</p>
                      <Button onClick={() => navigate(`/welcome?kind=${listTab === 'plugin' ? 'plugin' : 'app'}`)}>Start</Button>
                    </>
                  )}
                </div>
              );
            }
            return (
              <div className="myapps__grid">
                {items.map(item => {
                  const active = item.is_active !== false;
                  const initials = (item.name || '?').trim().slice(0, 2).toUpperCase();
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className="myapps__card"
                      onClick={() => (item._type === 'plugin'
                        ? navigate(`/plugins/${item.id}`)
                        : openDetail(item.id))}
                    >
                      {/* The uploaded logo, or the generated mark. The mark is
                          not a placeholder: most apps will never have a logo,
                          and it is what they look like — so it keeps the same
                          box and the same 22% squircle either way, and swapping
                          one for the other moves nothing in the row. */}
                      {item.logo_url ? (
                        <img
                          className="myapps__card-icon myapps__card-logo"
                          src={item.logo_url}
                          alt=""
                        />
                      ) : (
                        <span className="myapps__card-icon app-icon-grid" aria-hidden="true">
                          <span className="myapps__card-initials">{initials}</span>
                        </span>
                      )}
                      <span className="myapps__card-body">
                        <span className="myapps__card-name">{item.name}</span>
                        <span className="myapps__card-line">
                          {item._type === 'plugin' ? 'Plugin' : 'Application'}
                          {item.description ? ` · ${item.description}` : item.developer_email ? ` · ${item.developer_email}` : ''}
                        </span>
                        <span className="myapps__card-status">
                          <span className={`myapps__card-dot ${active ? 'is-active' : 'is-inactive'}`} />
                          {active ? 'Active' : 'Inactive'}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <Modal
        isOpen={epActionDialog.isOpen}
        onClose={() => setEpActionDialog({ isOpen: false, pendingPayload: null })}
        title="DLR Expansion — Existing Authorizations"
        size="sm"
        footer={
          <Button variant="secondary" onClick={() => setEpActionDialog({ isOpen: false, pendingPayload: null })}>
            Go Back
          </Button>
        }
      >
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>
          <p style={{ marginBottom: 12 }}>
            Your updated data request requires permissions that existing users haven't granted.
            How would you like to handle their current authorizations?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button variant="danger" onClick={() => submitWithEpAction('cancel')} style={{ width: '100%' }}>
              Cancel existing authorizations
            </Button>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>
              Users will need to re-authorize. The app will stop working for them until they do.
            </p>
            <Button variant="primary" onClick={() => submitWithEpAction('continue')} style={{ width: '100%' }}>
              Continue without canceling
            </Button>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
              Existing authorizations keep working for previously granted permissions. Requests using new permissions will be denied until users re-authorize.
            </p>
          </div>
        </div>
      </Modal>

      {showTransferModal && selectedItem && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transfer "{selectedItem.name}"</h3>
              <button className="modal-close" onClick={() => setShowTransferModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="transfer-type-tabs">
                <button
                  className={`transfer-tab ${transferType === 'user' ? 'transfer-tab--active' : ''}`}
                  onClick={() => { setTransferType('user'); setTransferTarget(''); setTransferError(''); }}
                >To a user</button>
                <button
                  className={`transfer-tab ${transferType === 'group' ? 'transfer-tab--active' : ''}`}
                  onClick={() => { setTransferType('group'); setTransferTarget(''); setTransferError(''); }}
                >To a group</button>
                {selectedItem.group_id && (
                  <button
                    className={`transfer-tab ${transferType === 'personal' ? 'transfer-tab--active' : ''}`}
                    onClick={() => { setTransferType('personal'); setTransferTarget(''); setTransferError(''); }}
                  >To my account</button>
                )}
              </div>

              {transferType === 'user' && (
                <div className="transfer-input-wrap">
                  <label className="transfer-label">Search for a user</label>
                  <div className="transfer-user-search">
                    <Input
                      value={userSearchQuery}
                      onChange={e => handleUserSearchChange(e.target.value)}
                      placeholder="Search by username or email…"
                      autoFocus
                      autoComplete="off"
                    />
                    {(userSearchResults.length > 0 || userSearchLoading) && !selectedUser && (
                      <div className="transfer-user-dropdown">
                        {userSearchLoading && <div className="transfer-user-option transfer-user-option--loading">Searching…</div>}
                        {userSearchResults.map(u => (
                          <button
                            key={u.user_id}
                            className="transfer-user-option"
                            type="button"
                            onClick={() => selectTransferUser(u)}
                          >
                            <span className="transfer-user-name">{u.username}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedUser && (
                      <div className="transfer-user-selected">
                        <span>{selectedUser.username}</span>
                        <button type="button" className="transfer-user-clear" onClick={() => { setSelectedUser(null); setTransferTarget(''); setUserSearchQuery(''); }}>×</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {transferType === 'group' && (
                <div className="transfer-input-wrap">
                  <label className="transfer-label">Select group</label>
                  <Select
                    value={transferTarget}
                    onChange={e => setTransferTarget(e.target.value)}
                  >
                    <option value="">— choose a group —</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </Select>
                </div>
              )}

              {transferType === 'personal' && (
                <p className="transfer-personal-note">
                  This will move the {selectedItem._type === 'plugin' ? 'plugin' : 'app'} from
                  the group to your personal account. You will become the sole owner.
                </p>
              )}

              {transferError && <p className="transfer-error">{transferError}</p>}
            </div>
            <div className="modal-footer">
              <Button variant="secondary" size="sm" onClick={() => setShowTransferModal(false)}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleTransfer}
                disabled={transferLoading || (transferType === 'user' && !transferTarget) || (transferType === 'group' && !transferTarget)}
              >
                {transferLoading ? 'Transferring…' : 'Transfer ownership'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PortalPage>
  );
}

// --- Overview ---------------------------------------------------------------
// Identity first — the values you copy into code — then everything you can
// change, each row saving only itself.
function ViewDetail({ item, copyToClipboard, saveField, stagedFields = [], onLogoSaved }) {
  const isApp = item._type === 'app';
  const uri = item.redirect_uri || item.redirect_uris?.[0] || '';

  // Plugins update through a different endpoint with a different shape, so they
  // keep the whole-form editor for now rather than half-working inline rows.
  const canInline = isApp && typeof saveField === 'function';
  // Each row's save snapshots the spec, so the button says which version it
  // will become rather than leaving that to be discovered in Versions.
  const field = (props) => (
    <InlineField
      {...props}
      onSave={saveField}
      editable={canInline}
      // The row still shows what the app serves. Without this marker a staged
      // edit looks like it was thrown away.
      staged={stagedFields.includes(props.name)}
    />
  );

  return (
    <>
      <section className="appfields">
        <h4 className="appfields__title">Identity</h4>
        <p className="appfields__note">Assigned by ofSelf. Use these in your code; they cannot be changed.</p>
        <ReadOnlyField label="Client ID" value={item.client_id} mono onCopy={copyToClipboard} />
        {isApp && <ReadOnlyField label="App ID" value={item.id} mono onCopy={copyToClipboard} />}
        {isApp && <ReadOnlyField label="API Key" value={item.api_key} mono secret onCopy={copyToClipboard} />}
        {isApp && <ReadOnlyField label="Webhook secret" value={item.webhook_secret} mono secret onCopy={copyToClipboard} />}
        <ReadOnlyField
          label="Created"
          value={item.created_at ? new Date(item.created_at).toLocaleDateString() : null}
        />
        {!isApp && <ReadOnlyField label="Owned by App" value={item.parent_app_name} />}
      </section>

      {/* Appearance stands apart from Settings: the logo and the shelf are
          presentation, not terms, so they apply at once and are not staged. */}
      <section className="appfields">
        <h4 className="appfields__title">Appearance</h4>
        <p className="appfields__note">Shown on the consent screen and in the app store. Applies at once, not staged.</p>
        {isApp && <AppLogoField app={item} onSaved={onLogoSaved} />}
        <CategoryField key={`cat-${item.id}`} item={item} onSaved={onLogoSaved} />
      </section>

      <section className="appfields">
        <h4 className="appfields__title">Settings</h4>
        <p className="appfields__note">
          {canInline
            ? 'Edits are staged, not applied. They go live together when you commit.'
            : 'Edit these with the form above.'}
        </p>

        {field({ label: 'Name', name: 'name', value: item.name })}
        {field({
          label: 'Description', name: 'description', value: item.description,
          type: 'textarea', placeholder: 'What this app does, in one line.',
        })}

        {isApp && field({
          label: 'Website', name: 'website_url', value: item.website_url,
          type: 'url', placeholder: 'https://example.com',
          render: v => v
            ? <a href={v} target="_blank" rel="noopener noreferrer" className="appfield__link">{v}</a>
            : null,
        })}

        {field({
          label: 'Repository', name: 'repository_url', value: item.repository_url,
          type: 'url', placeholder: 'https://github.com/org/repo',
          hint: 'Where the source lives. Reaching Clickable needs it, and dev.oS joins the coding sessions and pull requests that build this to it. Only the address is kept, and only its managers see it.',
          render: v => v
            ? <a href={v} target="_blank" rel="noopener noreferrer" className="appfield__link">{v}</a>
            : null,
        })}

        {isApp && field({
          label: 'Redirect URI', name: 'redirect_uri', value: uri,
          type: 'url', placeholder: 'https://example.com/callback',
          hint: 'Where people return to after authorizing. Must match exactly.',
          // The record carries both a scalar and a list; keep them in step.
          toPatch: v => ({ redirect_uri: v.trim(), redirect_uris: v.trim() ? [v.trim()] : [] }),
        })}

        {isApp && field({
          label: 'Deployment', name: 'deployment_mode', value: item.deployment_mode || 'incubator',
          type: 'select',
          options: [
            { value: 'incubator', label: 'Incubator — private, invite only' },
            { value: 'public', label: 'Public — listed in the app store' },
          ],
          render: v => v === 'public' ? 'Public' : 'Incubator (private)',
        })}

        {isApp && field({
          label: 'Who can find it', name: 'app_store_visibility',
          value: item.app_store_visibility || 'all_users',
          type: 'select',
          options: [
            { value: 'all_users', label: 'All users' },
            { value: 'selected_users', label: 'Selected users only' },
          ],
          render: v => v === 'selected_users' ? 'Selected users only' : 'All users',
        })}

        {isApp && item.app_store_visibility === 'selected_users' && (
          <ReadOnlyField label="Allowed Users" value={(item.allowed_user_ids || []).join(', ')} />
        )}

        {isApp && field({
          label: 'Webhook URL', name: 'webhook_url', value: item.webhook_url,
          type: 'url', placeholder: 'https://example.com/webhooks/ofself',
          hint: 'Where change events are delivered.',
        })}

        {isApp && item.webhook_events?.length > 0 && (
          <div className="appfield appfield--readonly">
            <span className="appfield__label">Webhook events</span>
            <div className="appfield__value">
              <code className="appfield__code">{item.webhook_events.join(', ')}</code>
            </div>
          </div>
        )}

        {!isApp && <ReadOnlyField label="Endpoint URL" value={item.plugin_endpoint_url} />}
        {!isApp && item.usage_guide && (
          <div className="appfield appfield--readonly">
            <span className="appfield__label">Agent Guide</span>
            <div className="appfield__value appfield__value--pre">{item.usage_guide}</div>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * A schema, named.
 *
 * A DLR stores schema UUIDs, so this rendered "nodes:read −9d155de0…, 6a8faf79…"
 * — which is the app losing read on code-push and code-pull-request, told in a
 * way that requires a second tool to understand. The endpoint now ships the
 * names for the ids this app's own history mentions; the truncated id is the
 * fallback for one the registry no longer has, and it keeps the full value on
 * hover so it stays greppable.
 */
const schemaName = (s, names) => {
  const raw = String(s);
  if (names && names[raw]) return names[raw];
  return /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(raw) ? `${raw.slice(0, 8)}…` : raw;
};

/** The schemas a permission reaches, named where the registry knows them. */
function Reach({ ids, names, prefix }) {
  if (!ids?.length) return null;
  return (
    <span className="versions__reach">
      {prefix}
      {ids.map((s, i) => (
        <React.Fragment key={s}>
          {i > 0 && ', '}
          <b title={String(s)}>{schemaName(s, names)}</b>
        </React.Fragment>
      ))}
    </span>
  );
}

/** Long prose does not belong on a history row; the field's own tab has it. */
function DiffValue({ v }) {
  if (v === null || v === undefined || v === '') return <em>empty</em>;
  if (typeof v === 'boolean') return <>{v ? 'yes' : 'no'}</>;
  const s = String(v);
  return s.length > 120 ? <span title={s}>{s.slice(0, 120)}…</span> : <>{s}</>;
}

/**
 * A wrapping set of marks. Fourteen section names is a wall even when they
 * wrap, so the count is the fact and the names are detail behind one click.
 */
const TAG_LIMIT = 5;
function Tags({ items, mark, tone }) {
  const [all, setAll] = useState(false);
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, TAG_LIMIT);
  const hidden = items.length - shown.length;
  return (
    <div className="versions__tags">
      {shown.map(t => (
        <span key={t} className={`versions__tag versions__tag--${tone}`}>
          <s aria-hidden="true">{mark}</s>{t}
        </span>
      ))}
      {(hidden > 0 || all) && (
        <button className="versions__more" onClick={() => setAll(a => !a)}>
          {all ? 'fewer' : `${hidden} more`}
        </button>
      )}
    </div>
  );
}

/** One field that moved, in the vocabulary of that field. */
function DiffRow({ change, schemaNames }) {
  const { kind, label, headline } = change;
  return (
    <div className={`versions__field versions__field--${changeKind(change)}`}>
      <span className="versions__field-name">{label}</span>
      <div className="versions__field-body">
        {kind === 'scalar' ? (
          <div className="versions__swap">
            <span className="versions__was"><DiffValue v={change.was} /></span>
            <span className="versions__arrow">→</span>
            <span className="versions__now"><DiffValue v={change.now} /></span>
          </div>
        ) : (
          <p className="versions__note">{headline}</p>
        )}

        {kind === 'dlr' && (
          <div className="versions__perms">
            {change.added.map(p => (
              <div key={`+${p.perm}`} className="versions__perm versions__perm--add">
                <s aria-hidden="true">+</s><code>{p.perm}</code>
                <Reach ids={p.schemas} names={schemaNames} prefix="on " />
              </div>
            ))}
            {change.removed.map(p => (
              <div key={`-${p.perm}`} className="versions__perm versions__perm--rm">
                <s aria-hidden="true">−</s><code>{p.perm}</code>
                <Reach ids={p.schemas} names={schemaNames} prefix="on " />
              </div>
            ))}
            {change.changed.map(p => (
              <div key={`~${p.perm}`} className="versions__perm versions__perm--edit">
                <s aria-hidden="true">~</s><code>{p.perm}</code>
                <span className="versions__reach">
                  {p.scope && <>scope <b>{p.scope.was || 'unset'}</b> → <b>{p.scope.now || 'unset'}</b>{(p.gained.length || p.lost.length) ? ' · ' : ''}</>}
                </span>
                {p.gained.length > 0 && <Reach ids={p.gained} names={schemaNames} prefix="+ " />}
                {p.lost.length > 0 && <Reach ids={p.lost} names={schemaNames} prefix="− " />}
              </div>
            ))}
            {/* The one consequence on this page, said where the change is. */}
            {change.changed.some(p => p.scope) || change.added.length > 0 ? (
              <p className="versions__warn">
                <b>Widening access</b> cancels or suspends existing authorizations.
              </p>
            ) : null}
          </div>
        )}

        {kind === 'crux' && (
          <>
            {change.written.length > 0 && (
              <div className="versions__set">
                <span className="versions__set-label">Written</span>
                <Tags items={change.written} mark="+" tone="add" />
              </div>
            )}
            {change.edited.length > 0 && (
              <div className="versions__set">
                <span className="versions__set-label">Rewritten</span>
                <Tags items={change.edited} mark="~" tone="edit" />
              </div>
            )}
            {change.cleared.length > 0 && (
              <div className="versions__set">
                <span className="versions__set-label">Cleared</span>
                <Tags items={change.cleared} mark="−" tone="rm" />
              </div>
            )}
          </>
        )}

        {kind === 'list' && (
          <>
            <Tags items={change.added} mark="+" tone="add" />
            <Tags items={change.removed} mark="−" tone="rm" />
          </>
        )}

        {kind === 'json' && (
          <details className="versions__raw">
            <summary>raw</summary>
            <pre>{JSON.stringify(change.was, null, 2)}</pre>
            <pre>{JSON.stringify(change.now, null, 2)}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * One change: what it was called, what it did, and what it did to the score.
 *
 * Headed "v16 → v17" because that is what the row is. The snapshot numbered 16
 * holds the state *before* its own summary's change, so one number left the
 * reader to work out whether the CRUX in it was the one before the rewrite or
 * after it. Roll back restores v16, which is now the side of the arrow it is
 * written on — so the button no longer has to repeat the number.
 */
function VersionRow({ v, after, score, prevScore, onRollback, schemaNames }) {
  const [open, setOpen] = useState(false);
  const changes = useMemo(() => (after ? diffVersions(v, after) : []), [v, after]);
  const delta = score && prevScore && prevScore.total ? score.passed - prevScore.passed : 0;
  const kind = eventKind(changes);

  return (
    <article className={`versions__event versions__event--${kind}${open ? ' is-open' : ''}`}>
      <div className="versions__head">
        <span className="versions__ver">v{v.version}<i>→</i>v{v.version + 1}</span>
        <span className="versions__stamp">
          {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
          {/* Who cut it. Recorded since the first version and never shown, so
              on a shared app the history said what changed and never who. */}
          <em>·</em>
          {v.changed_by_name || <i title="The account that made this change no longer exists">a removed account</i>}
        </span>
        {score && (
          <span
            className={`versions__score${delta > 0 ? ' is-up' : delta < 0 ? ' is-down' : ''}`}
            title={(score.failed || []).join(', ') || 'everything passing'}
          >
            <b>{score.passed}</b>/{score.total} checks
            <span className="versions__delta">{delta > 0 ? `+${delta}` : delta < 0 ? delta : '—'}</span>
          </span>
        )}
      </div>

      <p className="versions__summary">{v.change_summary || <em>No summary given</em>}</p>

      <div className="versions__chips">
        {changes.length > 0 ? (
          changes.map(c => (
            <button
              key={c.field}
              className={`versions__chip versions__chip--${changeKind(c)}`}
              aria-expanded={open}
              onClick={() => setOpen(o => !o)}
            >
              <span className="versions__caret" aria-hidden="true">▸</span>
              {c.label}
              {c.kind === 'dlr' && (c.added.length > 0 || c.removed.length > 0) && (
                <span className="versions__n">
                  {c.added.length > 0 && <b className="is-add">+{c.added.length}</b>}
                  {c.removed.length > 0 && <b className="is-rm">−{c.removed.length}</b>}
                </span>
              )}
              {c.kind === 'crux' && (
                <span className="versions__n">
                  {c.written.length > 0 && <b className="is-add">+{c.written.length}</b>}
                  {c.edited.length > 0 && <b>~{c.edited.length}</b>}
                  {c.cleared.length > 0 && <b className="is-rm">−{c.cleared.length}</b>}
                </span>
              )}
              {c.kind === 'list' && (
                <span className="versions__n">
                  {c.added.length > 0 && <b className="is-add">+{c.added.length}</b>}
                  {c.removed.length > 0 && <b className="is-rm">−{c.removed.length}</b>}
                </span>
              )}
            </button>
          ))
        ) : (
          <span className="versions__none">
            {after ? 'No spec fields changed' : 'Nothing to compare against'}
          </span>
        )}
        <button type="button" className="versions__rollback" onClick={() => onRollback(v.version)}>Roll back</button>
      </div>

      {open && changes.length > 0 && (
        <div className="versions__body">
          {changes.map(c => <DiffRow key={c.field} change={c} schemaNames={schemaNames} />)}
        </div>
      )}
    </article>
  );
}

/** A run of the model over the CRUX, in the history it belongs to. */
function ReviewRow({ run }) {
  return (
    <article className="versions__event versions__event--review">
      <div className="versions__head">
        <span className="versions__ver">model read the CRUX</span>
        <span className="versions__stamp">
          {run.ran_at ? new Date(run.ran_at).toLocaleString() : ''}<em>·</em>at v{run.version}
        </span>
        <span className="versions__score" title={(run.failed || []).join(', ') || 'everything passing'}>
          <b>{run.passed}</b>/{run.total} criteria met
        </span>
      </div>
      <p className="versions__summary versions__summary--review">
        {run.value?.assessed
          ? <>Societal value add <b className="versions__verdict">{run.value.level}/5</b> · {run.value.name}</>
          : <em>No level returned</em>}
      </p>
    </article>
  );
}

function VersionsPanel({ versions, loading, specVersion, onRollback, appId, live, schemaNames }) {
  // Eval history lives here rather than on OS-Evals. That tab answers "how is it
  // now"; this one is where everything historical already is, and a score is
  // only readable against the version that produced it.
  const [history, setHistory] = useState(null);
  const beginHistory = useLatest();
  useEffect(() => {
    const isCurrent = beginHistory();
    if (!appId) return;
    api.request(`/third-party/${appId}/readiness/history`)
      .then((h) => { if (isCurrent()) setHistory(h); })
      .catch(() => { if (isCurrent()) setHistory(null); });
  }, [appId, beginHistory]);

  const scoreFor = useMemo(() => {
    const m = {};
    for (const r of (history?.spec || [])) m[r.version] = r;
    return m;
  }, [history]);

  /* Version rows and review runs on one spine, ordered by when they happened.
   * A score that moved because the CRUX was reread is a different story from
   * one that moved because a schema vanished, and on two separate lists the
   * only way to tell them apart is to compare timestamps by hand. */
  const rows = useMemo(() => {
    const merged = [
      ...versions.map(v => ({ type: 'version', at: v.created_at, v })),
      ...(history?.review || []).map(r => ({ type: 'review', at: r.ran_at, run: r })),
    ];
    merged.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
    return merged;
  }, [versions, history]);

  // Newest first, so the version *after* vN is the row before it in the list —
  // and for the newest change of all, it is the app as it stands now.
  const byVersion = useMemo(() => new Map(versions.map(v => [v.version, v])), [versions]);

  if (loading) return <p className="appdetail__msg">Loading versions…</p>;
  if (!versions.length) {
    return <p className="appdetail__msg">No previous versions yet. One is written each time you save a change.</p>;
  }

  const nextState = (n) => byVersion.get(n + 1) || (n + 1 === (specVersion || 0) ? live : null);

  /* The key is a count, not a caption. "Seven access changes and six document
   * rewrites" is a fact about this app worth knowing before you read a single
   * row — and a legend that explains the colours to you would have been the
   * design apologising for itself. */
  const tally = { access: 0, crux: 0, meta: 0 };
  for (const v of versions) {
    const after = nextState(v.version);
    if (!after) continue;
    const changes = diffVersions(v, after);
    if (changes.length) tally[eventKind(changes)] += 1;
  }
  const reviews = (history?.review || []).length;
  const KEY = [
    ['access', 'access', tally.access],
    ['crux', 'CRUX', tally.crux],
    ['meta', 'metadata', tally.meta],
  ].filter(([, , n]) => n > 0);

  return (
    <div className="versions">
      <p className="versions__lede">
        Each row is one change: the version it started from, what moved, and how the
        structural checks stood at that version. Rolling back restores the version on
        the left of the arrow.
      </p>

      {(KEY.length > 0 || reviews > 0) && (
        <div className="versions__key">
          {KEY.map(([kind, label, n]) => (
            <span key={kind}>
              <i className={`versions__key-mark versions__key-mark--${kind}`} /><b>{n}</b> {label}
            </span>
          ))}
          {reviews > 0 && (
            <span>
              <i className="versions__key-mark versions__key-mark--review" /><b>{reviews}</b>
              {reviews === 1 ? ' review' : ' reviews'}
            </span>
          )}
        </div>
      )}

      {/* Where the history is now. Without it the spine starts in mid-air and
          nothing says which version the newest row is measured against. */}
      <article className="versions__event versions__event--now">
        <div className="versions__head">
          <span className="versions__ver">v{specVersion}</span>
          <span className="versions__stamp">live now</span>
        </div>
        <p className="versions__now-note">
          The spec this app is serving, and what every row below is measured back from.
        </p>
      </article>

      {rows.map(row => (
        row.type === 'review'
          ? <ReviewRow key={`r-${row.at}`} run={row.run} />
          : (
            <VersionRow
              key={row.v.id}
              v={row.v}
              after={nextState(row.v.version)}
              score={scoreFor[row.v.version]}
              prevScore={scoreFor[row.v.version - 1]}
              onRollback={onRollback}
              schemaNames={schemaNames}
            />
          )
      ))}
    </div>
  );
}

// ── Sandbox Test Users ──────────────────────────────────────────────────────

/**
 * Everything needed to start working on this app, with the ids already in it.
 *
 * The values are on the Settings tab and the commands are on Start developing,
 * and joining them up by hand — copy an id, find the command, paste it in the
 * right place — is a step that only ever goes wrong. This is that join, done.
 */
// ── The keyring ─────────────────────────────────────────────────────────────

/**
 * An app has one key per machine that runs it.
 *
 * This panel exists for the person who does NOT have the key — a new teammate, or
 * you on a second laptop. A key is stored hashed, so nothing (not this page, not
 * the CLI, not Paradigm) can show you an existing one. Before the keyring, the only
 * command that produced a credential also invalidated the working one, so the
 * practical answer was to message a colleague and paste a secret into chat, where
 * it stays forever. Minting is the answer instead: you are already authorized.
 */
function KeyringPanel({ appId, appName }) {
  const toast = useToast();
  const [keys, setKeys] = useState(null);       // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [minted, setMinted] = useState(null);   // shown ONCE, never fetched again
  const [error, setError] = useState(null);

  const begin = useLatest();
  const load = useCallback(async () => {
    const isCurrent = begin();
    if (!appId) return;
    setLoading(true);
    try {
      const res = await api.getAppKeys(appId);
      if (!isCurrent()) return;
      setKeys(res.keys || []);
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      // An empty list for a failed request would read as "this app has no keys",
      // which is a different statement from not being able to find out.
      setError(err.message || 'Could not load this app’s keys.');
      setKeys([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [appId, begin]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const label = name.trim();
    if (!label) {
      toast.error('Name the key — it is how you know which machine to revoke later.');
      return;
    }
    setCreating(true);
    try {
      const res = await api.createAppKey(appId, label);
      setMinted(res.api_key);
      setName('');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not create a key.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key) => {
    const ok = window.confirm(
      `Revoke “${key.name}”?\n\nWhatever holds this key stops working immediately. ` +
      `Every other key on ${appName} is unaffected.`
    );
    if (!ok) return;
    try {
      await api.revokeAppKey(appId, key.id);
      toast.success(`Revoked “${key.name}”.`);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not revoke that key.');
    }
  };

  const live = (keys || []).filter(k => k.is_live);

  return (
    <>
    <section className="testusers">
      <h4>New key</h4>
      <p className="testusers__lede">
        One key per machine that runs {appName}. Keys are stored hashed, so an existing key can
        never be shown again: <strong>make your own</strong> rather than asking for someone else's.
      </p>

      {minted && (
        <div className="keyring__minted">
          <strong>Copy this now — it will not be shown again.</strong>
          <pre><code>{minted}</code></pre>
          <p>
            Put it in <code>.paradigm/secrets.toml</code> under <code>[app] api_key</code>, and
            the SDK will read it from there — no <code>.env</code> copy to keep in sync. Or run{' '}
            <code>paradigm app key create</code> instead, which writes the file for you and never
            prints the value.
          </p>
          <div className="keyring__mintedactions">
            {/* Shown once: say "copied" only when the clipboard really took it. */}
            <button onClick={async () => {
              if (await copyText(minted)) toast.success('Key copied.');
              else toast.error('Could not copy the key — select it above and copy it by hand before pressing Done.');
            }}>
              Copy
            </button>
            <button onClick={() => setMinted(null)}>Done</button>
          </div>
        </div>
      )}

      <div className="devsession__block">
        <div className="keyring__new">
          <input
            type="text"
            value={name}
            placeholder="what holds it — a hostname, or “ci”"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          />
          <button onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create key'}
          </button>
        </div>
      </div>

    </section>

    <section className="testusers">
      <h4>Live keys {live.length > 0 && <span className="testusers__count">{live.length}</span>}</h4>
      {loading && keys === null ? (
        <p className="testusers__msg">Loading…</p>
      ) : error ? (
        <p className="testusers__msg testusers__msg--bad">{error}</p>
      ) : live.length === 0 ? (
        <p className="testusers__msg">No live keys.</p>
      ) : (
        <div className="testusers__list">
          {live.map(k => (
            <div key={k.id} className="testusers__row keyring__row">
              <span className="item-ic" aria-hidden="true"><KeyRound size={14} /></span>
              <div>
                <div className="testusers__who">
                  <span className="testusers__slot">{k.name}</span>
                  <code>{k.api_key_id}</code>
                </div>
                <div className="keyring__meta">
                  {/* "never used" is the fact that matters before revoking: a key
                      nothing has touched is one nobody will miss. */}
                  {k.last_used_at
                    ? `last seen ${new Date(k.last_used_at).toLocaleDateString()}`
                    : 'never used'}
                  {k.created_at ? ` · created ${new Date(k.created_at).toLocaleDateString()}` : ''}
                </div>
              </div>
              <button
                onClick={() => handleRevoke(k)}
                disabled={live.length <= 1}
                title={live.length <= 1
                  ? 'The only live key. Create a replacement first — revoking this would leave the app unable to authenticate.'
                  : 'Revoke this key'}
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
    </>
  );
}

/** What a test user's grant lets through, as one line: "nodes read, edit · activity read". */
const grantedText = (grants) => Object.entries(grants || {})
  .map(([resource, verbs]) => `${resource} ${verbs.join(', ')}`).join(' · ');

function TestUsersPanel({ appId, initialUserIds, onChanged }) {
  // Reset and delete wait for a yes: { kind: 'reset' | 'delete', userId, name }.
  const [confirmAct, setConfirmAct] = useState(null);
  const toast = useToast();
  const [testUsers, setTestUsers] = useState(null);   // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const begin = useLatest();
  const load = useCallback(async () => {
    const isCurrent = begin();
    if (!appId) return;
    setLoading(true);
    try {
      const res = await api.getTestUsers(appId);
      if (!isCurrent()) return;
      setTestUsers(res.test_users || []);
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      // Same trap as the authorized-users list: an empty array for a failed
      // request tells you the app has no test users, which is a different
      // statement from not being able to find out.
      setError(err.message || 'Could not load test users.');
      setTestUsers([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [appId, begin]);

  useEffect(() => {
    // WAS `if (expanded && …)`, and nothing ever set `expanded` — an expander
    // was removed and its guard left behind, so this never ran and the panel
    // rendered its empty state forever. A developer with three test users was
    // told they had none.
    if (testUsers === null) load();
  }, [testUsers, load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await api.createTestUser(appId);
      const copied = await copyText(res.user_id);
      toast.success(`Test user ${res.slot} created${copied ? ' — ID copied to clipboard' : ''}.`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Failed to create test user');
    } finally {
      setCreating(false);
    }
  };

  // Inline, because a browser prompt is a different application interrupting
  // you: it cannot be styled, cannot say what went wrong next to the field it
  // went wrong in, and throws the value away if you miss the OK button.
  const [editing, setEditing] = useState(null);   // {userId, kind, value, busy, error, done}

  const open = (userId, kind, value = '') =>
    setEditing({ userId, kind, value, busy: false, error: null, done: null });

  const commit = async () => {
    if (!editing || editing.busy) return;
    const { userId, kind, value } = editing;
    setEditing(e => ({ ...e, busy: true, error: null }));
    try {
      if (kind === 'password') {
        const res = await api.setTestUserPassword(appId, userId, value);
        setEditing(e => ({ ...e, busy: false, done: res.email }));
        return;                       // stay open to show the email to copy
      }
      await api.setTestUserLabel(appId, userId, value.trim());
      setEditing(null);
      await load();
      onChanged?.();
    } catch (err) {
      setEditing(e => ({ ...e, busy: false, error: err.message || 'Did not work.' }));
    }
  };

  // resetTestUser / deleteTestUser call until the wipe is done; `done: false`
  // back from them means it stopped unfinished, with `remaining` nodes left.
  const handleReset = async (userId) => {
    try {
      const res = await api.resetTestUser(appId, userId);
      const d = res.deleted || {};
      if (res.done === false) {
        toast.error(`Reset stopped with ${res.remaining ?? 'some'} nodes left (${d.nodes} deleted). Run it again to finish.`);
        return;
      }
      toast.success(`Reset: ${d.nodes || 0} nodes, ${d.relationships || 0} relationships deleted.`);
    } catch {
      toast.error('Reset failed');
    }
  };

  const handleDelete = async (userId) => {
    try {
      const res = await api.deleteTestUser(appId, userId);
      if (res.done === false) {
        toast.error(`Delete stopped with ${res.remaining ?? 'some'} nodes left; the test user still exists. Run it again to finish.`);
      } else {
        toast.success('Test user deleted.');
      }
      await load();
      onChanged?.();
    } catch {
      toast.error('Delete failed');
    }
  };

  const slotsFull = (testUsers || []).length >= 3;

  return (
    <section className="testusers">
      <h4>Sandbox users</h4>
      <p className="testusers__lede">
        Accounts that already granted this app everything it asks for. Send{' '}
        <code>X-User-ID: &lt;user_id&gt;</code> with your API calls to act as one.
        {testUsers?.length > 0 && ` ${testUsers.length} of 3 slots used.`}
      </p>
      {!slotsFull && (
        <PanelActions>
          <Button onClick={handleCreate} disabled={creating}>
            <Plus size={14} aria-hidden="true" /> {creating ? 'Creating…' : 'Add a test user'}
          </Button>
        </PanelActions>
      )}

      {loading && testUsers === null ? (
        <p className="testusers__msg">Loading…</p>
      ) : error ? (
        <p className="testusers__msg testusers__msg--bad">{error}</p>
      ) : testUsers === null ? (
        <p className="testusers__msg">Loading…</p>
      ) : testUsers.length === 0 ? (
        <div className="testusers__list">
          <div className="testusers__row has-ic testusers__row--empty">
            <span className="item-ic" aria-hidden="true"><UserRound size={14} /></span>
            <div className="testusers__body">
              <b>No test users yet</b>
              <span>Add one to call the API as a real person, without walking through consent each time. Its data is disposable.</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="testusers__list">
          {(testUsers || []).map(u => (
            <div key={u.user_id} className="testusers__row has-ic">
              <span className="item-ic" aria-hidden="true"><UserRound size={14} /></span>
              <div className="testusers__body">
              <div className="testusers__who">
                <span className="testusers__slot">{u.label || `User ${u.slot}`}</span>
                <code title={u.user_id}>{u.user_id.slice(0, 8)}…</code>
                {/* Quiet when it is the normal case: only a missing grant is news. */}
                {!u.authorized && <span className="testusers__state is-bad">No grant</span>}
              </div>

              {/* What it will actually let through — min(consent, ceiling), the
                  same thing the enforcement path computes. Without this a test
                  user is a uuid you have to take on trust. */}
              {u.grants && Object.keys(u.grants).length > 0 ? (
                <p className="testusers__grants" title={grantedText(u.grants)}>Granted: {grantedText(u.grants)}</p>
              ) : (
                <p className="testusers__grants-empty">
                  {u.authorized
                    ? 'Grants could not be read.'
                    : 'This user has not authorized the app, so calls as them will be refused.'}
                </p>
              )}

              <div className="testusers__actions">
                <button onClick={async () => {
                  if (await copyText(`X-User-ID: ${u.user_id}`)) toast.success('Header copied');
                  else toast.error('Could not copy the header.');
                }}>
                  Copy header
                </button>
                {/* Signing in as a sandbox user is a password you set, used in a
                    private window — there is no impersonation. */}
                <button onClick={() => open(u.user_id, 'password')} title="Set a password, then sign in as this user in a private window">
                  Sign in as
                </button>
                <button onClick={async () => {
                  if (await copyText(u.user_id)) toast.success('User ID copied');
                  else toast.error('Could not copy the user ID.');
                }}>
                  Copy id
                </button>
                <button onClick={() => open(u.user_id, 'label', u.label || '')}>Rename</button>
                {/* Both wipe what the user wrote, so both ask first. */}
                <button onClick={() => setConfirmAct({ kind: 'reset', userId: u.user_id, name: u.label || `User ${u.slot}` })}>
                  Reset data
                </button>
                <button className="is-danger" onClick={() => setConfirmAct({ kind: 'delete', userId: u.user_id, name: u.label || `User ${u.slot}` })}>
                  Delete
                </button>
              </div>

              {editing && editing.userId === u.user_id && (
                <form
                  className="testusers__edit"
                  onSubmit={e => { e.preventDefault(); commit(); }}
                >
                  {editing.done ? (
                    <p className="testusers__msg">
                      Password set. Sign in as <code>{editing.done}</code> —
                      a private window keeps it apart from your own session.{' '}
                      <button type="button" onClick={() => setEditing(null)}>Close</button>
                    </p>
                  ) : (
                    <>
                      <input
                        autoFocus
                        type={editing.kind === 'password' ? 'password' : 'text'}
                        value={editing.value}
                        placeholder={editing.kind === 'password'
                          ? 'New password for this sandbox user'
                          : 'A name for it, e.g. Kay — blank clears'}
                        aria-label={editing.kind === 'password'
                          ? 'New password' : 'Label'}
                        onChange={e => setEditing(s2 => ({ ...s2, value: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Escape') setEditing(null); }}
                      />
                      <button type="submit" disabled={editing.busy}>
                        {editing.busy ? 'Saving…' : 'Save'}
                      </button>
                      <button type="button" onClick={() => setEditing(null)}>Cancel</button>
                      {editing.error && (
                        <span className="testusers__msg testusers__msg--bad">
                          {editing.error}
                        </span>
                      )}
                    </>
                  )}
                </form>
              )}
              </div>
            </div>
          ))}
        </div>
      )}

      {slotsFull && (
        <p className="testusers__msg" style={{ marginTop: 10 }}>
          3 of 3 slots used — delete one to add another.
        </p>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirmAct)}
        onCancel={() => setConfirmAct(null)}
        onConfirm={() => {
          const act = confirmAct;
          setConfirmAct(null);
          if (act?.kind === 'reset') handleReset(act.userId);
          else if (act?.kind === 'delete') handleDelete(act.userId);
        }}
        title={confirmAct?.kind === 'delete' ? `Delete ${confirmAct?.name}?` : `Reset ${confirmAct?.name}'s data?`}
        message={confirmAct?.kind === 'delete'
          ? 'The sandbox user and everything it wrote are removed, and its slot is freed. This cannot be undone.'
          : 'Every node, tag and relationship this sandbox user wrote is deleted. The user and its grant stay. This cannot be undone.'}
        variant="danger"
        confirmLabel={confirmAct?.kind === 'delete' ? 'Delete' : 'Reset data'}
        cancelLabel="Keep it"
      />
    </section>
  );
}

function TestGroupsPanel({ appId, testUserIds, rev = 0 }) {
  // Members by the name their test user carries, not by uuid. Keyed on the ids
  // as one string, not the array: the caller builds a new array every render,
  // and an array dependency re-fetched the test users on every one of them.
  // `rev` moves when a test user is added, renamed or deleted, so a new name
  // shows here without a reload.
  const [names, setNames] = useState({});
  const idsKey = (testUserIds || []).join(',');
  useEffect(() => {
    let live = true;
    api.getTestUsers(appId).then((r) => {
      if (!live) return;
      const list = r?.test_users || r || [];
      setNames(Object.fromEntries(list.map(u => [u.user_id, u.label || `User ${u.slot}`])));
    }).catch(() => {});
    return () => { live = false; };
  }, [appId, idsKey, rev]);
  const nameOf = (uid) => names[uid] || `${String(uid).slice(0, 8)}…`;
  const toast = useToast();
  // A sandbox GROUP. A test user exercises the personal paths; a group agent, a
  // group realm and a group EP can only be reached through a group — and
  // borrowing a real one is exactly what makes a run's trace unrecordable, so
  // there was previously no safe way to test any of it.
  const [groups, setGroups] = useState(null);   // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  // Which test user admins the next group. The admin is the one member that
  // cannot be removed, so it is a choice, not a default.
  const [adminId, setAdminId] = useState('');

  const begin = useLatest();
  const load = useCallback(async () => {
    const isCurrent = begin();
    setLoading(true);
    setError(null);
    try {
      const res = await api.getTestGroups(appId);
      if (!isCurrent()) return;
      setGroups(res.test_groups || []);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err.message || 'Could not load test groups.');
      setGroups([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [appId, begin]);

  useEffect(() => { if (groups === null) load(); }, [groups, load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.createTestGroup(appId, adminId || (testUserIds || [])[0]);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to create test group');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (groupId) => {
    try {
      await api.deleteTestGroup(appId, groupId);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete test group');
    }
  };

  const [renaming, setRenaming] = useState(null);   // {groupId, value, busy, error}

  const commitRename = async () => {
    if (!renaming || renaming.busy) return;
    setRenaming(r => ({ ...r, busy: true, error: null }));
    try {
      await api.setTestGroupLabel(appId, renaming.groupId, renaming.value.trim());
      setRenaming(null);
      await load();
    } catch (e) {
      setRenaming(r => ({ ...r, busy: false, error: e.message || 'Did not work.' }));
    }
  };

  const handleAdd = async (groupId, userId) => {
    try {
      await api.addTestGroupMember(appId, groupId, userId);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to add member');
    }
  };

  const slotsFull = (groups || []).length >= 3;
  const noTestUsers = (testUserIds || []).length === 0;

  return (
    <section className="testusers">
      <h4>Sandbox groups</h4>
      <p className="testusers__lede">
        A group made only of this app&apos;s test users, to try group agents, group realms
        and group grants. Runs inside one are recorded for you to read.
      </p>

      {noTestUsers ? (
        <p className="testusers__msg">Needs a test user first: one of them runs the group.</p>
      ) : loading && groups === null ? (
        <p className="testusers__msg">Loading…</p>
      ) : error ? (
        <p className="testusers__msg testusers__msg--bad">{error}</p>
      ) : (groups || []).length === 0 ? (
        <p className="testusers__msg">None yet.</p>
      ) : (
        <div className="testusers__list">
          {(groups || []).map(g => (
            <div key={g.group_id} className="testusers__row has-ic">
              <span className="item-ic" aria-hidden="true"><Users size={14} /></span>
              <div className="testusers__body">
              <div className="testusers__who">
                <span className="testusers__slot">{g.label || `Group ${g.slot}`}</span>
                <code title={g.group_id}>{g.group_id.slice(0, 8)}…</code>
              </div>
              <p className="testusers__grants">
                {(g.members || []).map(uid => nameOf(uid)).join(', ') || 'No members'}
              </p>
              <div className="testusers__actions">
                {(testUserIds || [])
                  .filter(uid => !(g.members || []).includes(uid))
                  .map(uid => (
                    <button key={uid} onClick={() => handleAdd(g.group_id, uid)}>
                      Add {nameOf(uid)}
                    </button>
                  ))}
                <button onClick={() => setRenaming({ groupId: g.group_id,
                                                     value: g.label || '',
                                                     busy: false, error: null })}>
                  Rename
                </button>
                <button className="is-danger" onClick={() => handleDelete(g.group_id)}>
                  Delete
                </button>
              </div>

              {renaming && renaming.groupId === g.group_id && (
                <form
                  className="testusers__edit"
                  onSubmit={e => { e.preventDefault(); commitRename(); }}
                >
                  <input
                    autoFocus
                    value={renaming.value}
                    placeholder="A name for this group — blank clears"
                    aria-label="Group label"
                    onChange={e => setRenaming(r => ({ ...r, value: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Escape') setRenaming(null); }}
                  />
                  <button type="submit" disabled={renaming.busy}>
                    {renaming.busy ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" onClick={() => setRenaming(null)}>Cancel</button>
                  {renaming.error && (
                    <span className="testusers__msg testusers__msg--bad">{renaming.error}</span>
                  )}
                </form>
              )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!noTestUsers && !slotsFull ? (
        <div style={{ marginTop: 12 }}>
          {(testUserIds || []).length > 1 && (
            <select
              value={adminId || (testUserIds || [])[0] || ''}
              onChange={e => setAdminId(e.target.value)}
              aria-label="Which test user admins the group"
              style={{ marginRight: 8 }}
            >
              {(testUserIds || []).map(uid => (
                <option key={uid} value={uid}>{`Run by ${nameOf(uid)}`}</option>
              ))}
            </select>
          )}
          <Button variant="secondary" size="sm" onClick={handleCreate}
                  disabled={creating}>
            {creating ? 'Creating…' : 'Add a test group'}
          </Button>
        </div>
      ) : !noTestUsers ? (
        <p className="testusers__msg" style={{ marginTop: 10 }}>
          3 of 3 slots used — delete one to add another.
        </p>
      ) : null}
    </section>
  );
}

function AppGraphPanel({ appId }) {
  const toast = useToast();
  const [nodes, setNodes] = useState(null);   // null = not loaded yet
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [schemaName, setSchemaName] = useState('');
  const [valueJsonText, setValueJsonText] = useState('{}');

  // Paginated on purpose — an app's catalog is meant to hold hundreds or
  // thousands of entries (a full skill/body-part vocabulary), and rendering
  // all of it as one flat DOM list would be the thing that doesn't scale,
  // not the catalog itself. One page in the DOM at a time, always.
  // A later page asked for while an earlier one is out wins: the earlier answer
  // is dropped rather than painted over it.
  const begin = useLatest();
  const load = useCallback(async (atOffset = 0) => {
    const isCurrent = begin();
    if (!appId) return;
    setLoading(true);
    try {
      const res = await api.getAppNodes(appId, { limit: CATALOG_PAGE_SIZE, offset: atOffset });
      if (!isCurrent()) return;
      setNodes(res.nodes || []);
      setTotal(res.total || 0);
      setOffset(atOffset);
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err.message || 'Could not load the catalog.');
      setNodes([]);
      setTotal(0);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [appId, begin]);

  useEffect(() => { load(0); }, [load]);

  const handlePublish = async () => {
    if (!title.trim()) {
      toast.error('Title is required.');
      return;
    }
    let valueJson;
    try {
      valueJson = valueJsonText.trim() ? JSON.parse(valueJsonText) : {};
    } catch {
      toast.error('value_json is not valid JSON.');
      return;
    }
    setPublishing(true);
    try {
      await api.createAppNode(appId, { title, schemaName: schemaName || undefined, valueJson });
      toast.success(`Published '${title}'.`);
      setTitle(''); setSchemaName(''); setValueJsonText('{}');
      await load(0); // newest-first ordering puts it on page one
    } catch (e) {
      toast.error(e?.message || 'Failed to publish node');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async (nodeId, nodeTitle) => {
    try {
      await api.deleteAppNode(appId, nodeId);
      toast.success(`Deleted '${nodeTitle}'. References into it will stop resolving.`);
      // Step back a page if deleting emptied the one we were looking at.
      const nowEmpty = nodes && nodes.length === 1;
      await load(nowEmpty && offset > 0 ? offset - CATALOG_PAGE_SIZE : offset);
    } catch {
      toast.error('Delete failed');
    }
  };

  const [previewing, setPreviewing] = useState(false);
  const handlePreview = async () => {
    setPreviewing(true);
    try {
      // 'dashboard' is nucleus's own audience — this hands YOU off to nucleus
      // as yourself, never the app's headless account (which can never log in
      // at all). Nucleus resolves what to render from the appId in the URL.
      const target = `${NUCLEUS_URL}/app/${appId}/graph`;
      const { code } = await api.createLoginHandoffCode('dashboard', target);
      window.open(`${target}?code=${encodeURIComponent(code)}`, '_blank', 'noopener');
    } catch (e) {
      toast.error(e?.message || 'Could not open the preview.');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <>
    <PanelActions>
      <Button variant="secondary" onClick={handlePreview} disabled={previewing}>
        {previewing ? 'Opening…' : 'Preview in Nucleus ↗'}
      </Button>
    </PanelActions>
    <section className="testusers">
      <h4>Published {total > 0 && <span className="testusers__count">{total}</span>}</h4>
      <p className="testusers__lede">
        Nodes that belong to the app, not to any one person: the vocabulary it ships, which a
        person's own nodes can link to.
      </p>

      {loading && nodes === null ? (
        <p className="testusers__msg">Loading…</p>
      ) : error ? (
        <p className="testusers__msg testusers__msg--bad">{error}</p>
      ) : (nodes || []).length === 0 ? (
        <p className="testusers__msg">Nothing published yet.</p>
      ) : (
        <>
          <div className="ptable" role="table" aria-label="Published nodes">
            <div className="ptable__row ptable__row--head" role="row">
              <span role="columnheader">Title</span><span role="columnheader">Schema</span><span role="columnheader" />
            </div>
            {(nodes || []).map(n => (
              <div key={n.id} className="ptable__row" role="row">
                <span role="cell" className="ptable__strong">{n.title}</span>
                <span role="cell" className="ptable__mono">{n.schema_name || '—'}</span>
                <span role="cell" className="ptable__acts">
                  <button onClick={async () => {
                    if (await copyText(n.id)) toast.success('Node id copied');
                    else toast.error('Could not copy the node id.');
                  }}>Copy id</button>
                  <button className="is-danger" onClick={() => handleDelete(n.id, n.title)}>Delete</button>
                </span>
              </div>
            ))}
          </div>
          {total > CATALOG_PAGE_SIZE && (
            <div className="ptable__pager">
              <button disabled={loading || offset === 0} onClick={() => load(Math.max(0, offset - CATALOG_PAGE_SIZE))}>← Previous</button>
              <span>{offset + 1}–{Math.min(offset + CATALOG_PAGE_SIZE, total)} of {total}</span>
              <button disabled={loading || offset + CATALOG_PAGE_SIZE >= total} onClick={() => load(offset + CATALOG_PAGE_SIZE)}>Next →</button>
            </div>
          )}
        </>
      )}
    </section>

    <section className="testusers">
      <h4>Publish a node</h4>
      <div className="publishrow">
        <Input label="Title" type="text" value={title}
          onChange={e => setTitle(e.target.value)} placeholder="Travel" />
        <Input label="Schema (optional)" type="text" value={schemaName}
          onChange={e => setSchemaName(e.target.value)} placeholder="An existing schema name" />
        <Button onClick={handlePublish} disabled={publishing}>
          {publishing ? 'Publishing…' : 'Publish'}
        </Button>
      </div>
      <details className="publishrow__more">
        <summary>Add fields (value_json)</summary>
        <Textarea label="value_json" value={valueJsonText}
          onChange={e => setValueJsonText(e.target.value)} rows={3} />
      </details>
    </section>
    </>
  );
}
const CATALOG_PAGE_SIZE = 25;




export default MyAppsTab;
